import React, { memo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  Dropdown,
  SlideUp,
  SlideUpHeaderBackButton,
  Text
} from 'common-components'
import { useDropdownState } from 'hooks/useDropdownState'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { editorThunks } from 'store/features/editor/thunks'
import { dispatch } from 'store'
import { IModelDropdownProps } from './types'
import { useEventListener } from 'hooks'
import ModelDropdownContent from './ModelDropdownContent'
import { useScrollableElement } from 'containers/SubSidebar/AIVideoContent/context'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'

const ModelDropdown: React.FC<IModelDropdownProps> = ({
  models,
  selectedModelCode,
  onModelSelect,
  className = ''
}) => {
  const { scrollableElementRef } = useScrollableElement()
  const { isOpen, closeDropdown, toggleDropdown } = useDropdownState(
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(true)),
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(false))
  )

  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const handleSelect = useCallback(
    (modelCode: string | null) => {
      if (modelCode !== selectedModelCode) {
        onModelSelect(modelCode)
      }
      closeDropdown()
    },
    [selectedModelCode, onModelSelect, closeDropdown]
  )

  useEventListener(
    'scroll',
    closeDropdown,
    !isTablet ? scrollableElementRef.current : null,
    true
  )

  const content = (
    <ModelDropdownContent
      models={models}
      selectedModelCode={selectedModelCode}
      onSelect={handleSelect}
    />
  )

  const selectedModel = models.find(model => model.model === selectedModelCode)
  const displayValue = selectedModel?.name || translate(T.ALL_LABEL)

  if (isTablet && isOpen) {
    return createPortal(
      <SlideUp isOpen={isOpen} onClose={closeDropdown}>
        <SlideUpHeaderBackButton onBack={closeDropdown} />
        {content}
      </SlideUp>,
      document.body
    )
  }

  return (
    <Dropdown
      className={className}
      headerContent={
        <Text type="h2" color="secondaryDark">
          {displayValue}
        </Text>
      }
      mainContent={content}
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      closeDropdown={closeDropdown}
      forceBottom
    />
  )
}

export default memo(ModelDropdown)
